import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Search, Plus, Send, Image as ImageIcon, Pin, Archive, BellOff, Ban, Flag, Smile, Check, CheckCheck, X, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const REACTIONS = ["👍", "❤️", "😂", "🔥", "🎉", "😮"];
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now"; if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`; return `${Math.floor(s / 86400)}d`;
};

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convos, setConvos] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [convSearch, setConvSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingAtt, setPendingAtt] = useState([]);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const typingRef = useRef(0);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/messages/conversations"); setConvos(data.conversations); return data.conversations; }
    catch { setConvos([]); return []; }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const [m, c] = await Promise.all([
        api.get(`/messages/conversations/${id}/messages`, { params: { limit: 80 } }),
        api.get(`/messages/conversations/${id}`),
      ]);
      setMsgs(m.data.messages); setActive(c.data);
      api.post(`/messages/conversations/${id}/read`).then(loadConvos).catch(() => {});
    } catch { /* ignore */ }
  }, [loadConvos]);

  // initial + ?to= deep link to start a DM
  useEffect(() => {
    (async () => {
      const list = await loadConvos();
      const to = params.get("to");
      if (to) {
        try { const { data } = await api.post("/messages/conversations", { type: "dm", member_ids: [to] }); setActiveId(data.id); await loadConvos(); }
        catch (e) { toast.error(e.response?.data?.detail || "Couldn't start chat"); }
        params.delete("to"); setParams(params, { replace: true });
      } else if (list.length && !activeId) {
        setActiveId(list[0].id);
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);

  // polling: active thread + conversation list
  useEffect(() => {
    const t = setInterval(() => { if (activeId) loadThread(activeId); loadConvos(); }, 3000);
    return () => clearInterval(t);
  }, [activeId, loadThread, loadConvos]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, activeId]);

  const onType = (v) => {
    setText(v);
    const now = Date.now();
    if (activeId && now - typingRef.current > 3000) { typingRef.current = now; api.post(`/messages/conversations/${activeId}/typing`).catch(() => {}); }
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && pendingAtt.length === 0) || !activeId) return;
    setText(""); const atts = pendingAtt; setPendingAtt([]);
    try {
      const { data } = await api.post(`/messages/conversations/${activeId}/messages`, { text: body, attachments: atts });
      setMsgs((m) => [...m, data]); loadConvos();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("name", file.name); fd.append("type", "message");
    try {
      const { data } = await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingAtt((a) => [...a, { type: "image", url: data.url, name: data.name }]);
      toast.success("Image attached 📎");
    } catch { toast.error("Upload failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const react = async (msg, emoji) => {
    try { const { data } = await api.post(`/messages/messages/${msg.id}/react`, { emoji });
      setMsgs((m) => m.map((x) => x.id === msg.id ? { ...x, reactions: data.reactions } : x)); } catch { /* ignore */ }
  };

  const convAction = async (action) => {
    if (!activeId) return;
    try { await api.post(`/messages/conversations/${activeId}/${action}`); toast.success(`Conversation ${action}d`); await loadThread(activeId); await loadConvos(); }
    catch { toast.error("Action failed"); }
    setMenuOpen(false);
  };

  const blockUser = async () => {
    const other = active?.others?.[0]; if (!other) return;
    try { await api.post(`/messages/block/${other.id}`); toast("User blocked"); } catch { /* ignore */ }
    setMenuOpen(false);
  };
  const reportUser = async () => {
    const other = active?.others?.[0]; if (!other) return;
    try { await api.post("/messages/report", { user_id: other.id, reason: "Reported from chat" }); toast("Report submitted"); } catch { /* ignore */ }
    setMenuOpen(false);
  };

  const filtered = (convos || []).filter((c) => c.title.toLowerCase().includes(convSearch.toLowerCase()));
  const otherRead = (msg) => active?.type === "dm" && active.others?.[0] && (msg.read_by || []).includes(active.others[0].id);

  return (
    <div className="h-[calc(100vh-4rem)] flex" data-testid="messages-page">
      {/* conversation list */}
      <aside className="w-full sm:w-80 shrink-0 border-r border-[hsl(var(--border))] flex flex-col">
        <div className="p-4 flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold flex items-center gap-2"><MessageSquare className="w-6 h-6 text-[hsl(var(--primary))]" /> Messages</h1>
          <Button size="icon" className="rounded-full h-9 w-9" onClick={() => setShowNew(true)} data-testid="new-chat-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input value={convSearch} onChange={(e) => setConvSearch(e.target.value)} placeholder="Search chats…" className="pl-9 h-9" data-testid="conv-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="conv-list">
          {convos == null ? <Spinner /> : filtered.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] text-center px-4 py-8">No conversations yet. Tap + to start one!</p>
          ) : filtered.map((c) => (
            <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`conv-item-${c.id}`}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[hsl(var(--border))]/40 transition-colors ${activeId === c.id ? "bg-[hsl(var(--secondary))]" : "hover:bg-[hsl(var(--secondary))]/50"}`}>
              <div className="relative shrink-0">
                <div className="w-11 h-11 rounded-full grid place-items-center text-xl bg-gradient-to-br from-[#1f6feb] to-[#3b9eff]">{c.avatar}</div>
                {c.online && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[hsl(var(--background))]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {c.pinned && <Pin className="w-3 h-3 text-[hsl(var(--primary))]" />}
                  <span className="font-bold truncate">{c.title}</span>
                  {c.muted && <BellOff className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.typing.length > 0 ? "typing…" : (c.last_message?.text || "No messages yet")}</div>
              </div>
              {c.unread > 0 && <span className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-bold" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
            </button>
          ))}
        </div>
      </aside>

      {/* thread */}
      <section className="hidden sm:flex flex-1 flex-col">
        {!active ? (
          <div className="flex-1 grid place-items-center"><EmptyState emoji="💬" title="Select a conversation" text="Pick a chat on the left or start a new one." /></div>
        ) : (
          <>
            <header className="h-16 px-5 flex items-center justify-between border-b border-[hsl(var(--border))]" data-testid="thread-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center text-lg bg-gradient-to-br from-[#1f6feb] to-[#3b9eff]">{active.avatar}</div>
                <div>
                  <div className="font-bold flex items-center gap-1.5">{active.type === "group" && <Users className="w-4 h-4" />}{active.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {active.typing.length > 0 ? <span data-testid="typing-indicator">typing…</span> : active.type === "dm" ? (active.online ? <span className="text-emerald-500" data-testid="online-dot">● Online</span> : "Offline") : `${active.members.length} members`}
                  </div>
                </div>
              </div>
              <div className="relative">
                <Button variant="ghost" size="icon" onClick={() => setMenuOpen((v) => !v)} data-testid="conv-menu-btn"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg></Button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1 w-48 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl z-30 py-1 text-sm" data-testid="conv-menu">
                    <button onClick={() => convAction("pin")} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] flex items-center gap-2" data-testid="menu-pin"><Pin className="w-4 h-4" /> {active.pinned ? "Unpin" : "Pin"}</button>
                    <button onClick={() => convAction("archive")} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] flex items-center gap-2" data-testid="menu-archive"><Archive className="w-4 h-4" /> {active.archived ? "Unarchive" : "Archive"}</button>
                    <button onClick={() => convAction("mute")} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] flex items-center gap-2" data-testid="menu-mute"><BellOff className="w-4 h-4" /> {active.muted ? "Unmute" : "Mute"}</button>
                    {active.type === "dm" && <>
                      <button onClick={blockUser} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] flex items-center gap-2 text-rose-500" data-testid="menu-block"><Ban className="w-4 h-4" /> Block</button>
                      <button onClick={reportUser} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] flex items-center gap-2 text-rose-500" data-testid="menu-report"><Flag className="w-4 h-4" /> Report</button>
                    </>}
                  </div>
                )}
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="msg-list">
              {msgs.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
                    <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      {!mine && active.type === "group" && <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-1 mb-0.5">{m.sender_name}</span>}
                      <div className={`relative rounded-2xl px-3.5 py-2 ${mine ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))]"}`}>
                        {(m.attachments || []).map((a, i) => a.type === "image" && (
                          <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="rounded-lg mb-1 max-h-48" />
                        ))}
                        {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                        <div className={`flex items-center gap-1 mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>
                          {timeAgo(m.created_at)}
                          {mine && (otherRead(m) ? <CheckCheck className="w-3 h-3" data-testid="read-receipt" /> : <Check className="w-3 h-3" />)}
                        </div>
                        {/* reaction picker */}
                        <div className={`absolute ${mine ? "right-full mr-1" : "left-full ml-1"} top-0 opacity-0 group-hover:opacity-100 transition-opacity flex bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-full shadow px-1`}>
                          {REACTIONS.map((e) => <button key={e} onClick={() => react(m, e)} data-testid={`react-${e}`} className="px-0.5 hover:scale-125 transition-transform text-sm">{e}</button>)}
                        </div>
                      </div>
                      {Object.keys(m.reactions || {}).length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {Object.entries(m.reactions).map(([e, us]) => (
                            <button key={e} onClick={() => react(m, e)} className="text-xs bg-[hsl(var(--secondary))] rounded-full px-1.5 py-0.5 border border-[hsl(var(--border))]">{e} {us.length}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {pendingAtt.length > 0 && (
              <div className="px-5 py-2 flex gap-2 border-t border-[hsl(var(--border))]">
                {pendingAtt.map((a, i) => (
                  <div key={i} className="relative"><img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <button onClick={() => setPendingAtt((p) => p.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 w-4 h-4 grid place-items-center rounded-full bg-rose-500 text-white"><X className="w-3 h-3" /></button></div>
                ))}
              </div>
            )}
            <div className="p-4 border-t border-[hsl(var(--border))] flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImage} data-testid="attach-input" />
              <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} data-testid="attach-btn"><ImageIcon className="w-5 h-5" /></Button>
              <Input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Type a message…" data-testid="msg-input" />
              <Button onClick={send} className="rounded-full" data-testid="send-msg-btn"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </section>

      <NewChatDialog open={showNew} onClose={() => setShowNew(false)} onCreated={async (id) => { setShowNew(false); await loadConvos(); setActiveId(id); }} />
    </div>
  );
}

function NewChatDialog({ open, onClose, onCreated }) {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setSelected([]); setGroupName(""); setQ(""); return; }
    (async () => {
      setLoading(true);
      try {
        const [fo, fr] = await Promise.all([api.get("/social/following"), api.get("/social/friends")]);
        const map = {};
        [...(fo.data.following || []), ...(fr.data.friends || [])].forEach((u) => { map[u.id] = u; });
        setPeople(Object.values(map));
      } catch { setPeople([]); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const create = async () => {
    if (selected.length === 0) return;
    const type = selected.length > 1 ? "group" : "dm";
    try { const { data } = await api.post("/messages/conversations", { type, member_ids: selected, name: groupName }); onCreated(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create chat"); }
  };
  const filtered = people.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-chat-dialog">
        <DialogHeader><DialogTitle>New message</DialogTitle></DialogHeader>
        {selected.length > 1 && <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name (optional)" className="mb-2" data-testid="group-name-input" />}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search friends & follows…" className="mb-2" />
        <div className="max-h-72 overflow-y-auto space-y-1">
          {loading ? <Spinner /> : filtered.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">Follow people or add friends to message them.</p> :
            filtered.map((p) => (
              <button key={p.id} onClick={() => toggle(p.id)} data-testid={`new-chat-person-${p.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl ${selected.includes(p.id) ? "bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/40" : "hover:bg-[hsl(var(--secondary))]"}`}>
                <span className="w-9 h-9 rounded-full grid place-items-center text-lg bg-gradient-to-br from-[#1f6feb] to-[#3b9eff]">{p.avatar_emoji}</span>
                <span className="font-semibold flex-1 text-left">{p.name}</span>
                {selected.includes(p.id) && <Check className="w-4 h-4 text-[hsl(var(--primary))]" />}
              </button>
            ))}
        </div>
        <Button onClick={create} disabled={selected.length === 0} className="w-full rounded-full mt-2" data-testid="create-chat-btn">
          {selected.length > 1 ? `Create group (${selected.length})` : "Start chat"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
